<template>
  <div :class="style.questionSetting">
    <div :class="style.tabsPanel">
      <TabPanel
        text="Настройки"
        :active="selectedTabPanel === 'Настройки'"
        @click="selectedTabPanel = 'Настройки'"
      />

      <TabPanel
        text="Контент"
        :active="selectedTabPanel === 'Контент'"
        @click="selectedTabPanel = 'Контент'"
      />
    </div>

    <div :class="style.settingPart" v-if="selectedTabPanel === 'Настройки'">
      <SelectBase
        comment="Тип блока"
        :class="style.block"
        :text="currentQuestionTypeText"
        :items="typeQuestions"
        @select="setTypeQuestion"
      />

      <QuestionSettingPart
        :question="currentQuestion"
        :question-index="questionIndex"
      />

      <div :class="[style.block, style.switchBlock]">
        <span :class="style.heading">Обязательно</span>

        <SwitchComponent
          :model-value="isRequiredQuestion"
          @update:modelValue="setRequiredQuestion"
        />
      </div>
    </div>

    <div :class="style.contentPart" v-if="selectedTabPanel === 'Контент'">
      <QuestionContentComponent
        :question="currentQuestion"
        :question-index="questionIndex"
      />
    </div>
  </div>
</template>

<script lang="ts">
import {defineComponent} from "vue";

import style from "./QuestionPanelComponent.module.scss";

import TabPanel from "@/elements/UI/tabs/TabPanel/TabPanel.vue";
import SelectBase from "@/elements/UI/select/SelectBase.vue";
import SwitchComponent from "@/elements/UI/input/Switch/SwitchComponent.vue";

import QuestionContentPart from "@/page/quiz/editor/landingPage/components/elements/SettingPanelComponent/blocks/QuestionSetting/components/QuestionContentPart.vue";
import QuestionSettingPart from "@/page/quiz/editor/landingPage/components/elements/SettingPanelComponent/blocks/QuestionSetting/components/QuestionSettingPart.vue";

import {DataPanelClues, questions, QuestionTypes} from "@/page/quiz/typeQuiz";

export default defineComponent({
  name: "QuestionPanelComponent",

  components: {
    QuestionContentComponent: QuestionContentPart,
    QuestionSettingPart,
    TabPanel,
    SelectBase,
    SwitchComponent
  },

  data() {
    return {
      style,
      selectedTabPanel: "Настройки" as SelectedPanel
    };
  },

  methods: {
    isOpenListQuestionType(type: unknown): boolean {
      return (
        type === "radio" ||
        type === "checkbox" ||
        type === "radio-img" ||
        type === "checkbox-img"
      );
    },
    isTextQuestionType(type: QuestionTypes): boolean {
      return type === "text" || type === "textArea";
    },
    setTypeQuestion(payload: SelectBasePayload): void {
      if (!this.currentQuestion) {
        return;
      }

      const newType = this.getQuestionTypeFromPayload(payload);

      if (!newType) {
        return;
      }

      if (this.currentQuestion.type === newType) {
        return;
      }

      this.currentQuestion.type = newType;
      this.normalizeQuestionByType(newType);
    },

    getQuestionTypeFromPayload(payload: SelectBasePayload): QuestionTypes | "" {
      const value = payload.value;

      if (typeof value !== "string") {
        return "";
      }

      if (!this.isQuestionType(value)) {
        return "";
      }

      return value;
    },

    isQuestionType(value: string): value is QuestionTypes {
      return this.typeQuestions.some((item) => {
        return item.value === value;
      });
    },

    setRequiredQuestion(value: boolean | Event): void {
      if (!this.currentQuestion) {
        return;
      }

      const required =
        value instanceof Event
          ? Boolean((value.target as HTMLInputElement | null)?.checked)
          : Boolean(value);

      this.currentQuestion.skip = !required;
    },

    normalizeQuestionByType(type: QuestionTypes): void {
      if (!this.currentQuestion) {
        return;
      }

      if (type === "select" || this.isOpenListQuestionType(type)) {
        this.ensureVariants();
        return;
      }

      if (type === "text" || this.isTextQuestionType(type)) {
        this.ensureTextQuestion();
        return;
      }

      if (type === "checkbox-dental") {
        this.ensureDentalQuestion();
        return;
      }

      if (type === "information") {
        this.ensureInformationQuestion();
      }
    },

    ensureVariants(): void {
      if (!this.currentQuestion) {
        return;
      }

      if (
        Array.isArray(this.currentQuestion.variants) &&
        this.currentQuestion.variants.length > 0
      ) {
        return;
      }
    },

    ensureTextQuestion(): void {
      if (!this.currentQuestion) {
        return;
      }
    },

    ensureDentalQuestion(): void {
      if (!this.currentQuestion) {
        return;
      }

      // if (!this.currentQuestion.selectedTeeth) {
      //   this.currentQuestion.selectedTeeth = [];
      // }
    },

    ensureInformationQuestion(): void {
      if (!this.currentQuestion) {
        return;
      }

      // if (!this.currentQuestion.text) {
      //   this.currentQuestion.text = "";
      // }
    }
  },

  computed: {
    quiz() {
      return this.$quizStore.getQuiz;
    },

    questionIndex(): number {
      return this.dataPanel.index;
    },

    currentQuestion(): questions | null {
      if (!this.quiz?.questions?.length) {
        return null;
      }

      if (this.questionIndex < 0) {
        return null;
      }

      return this.quiz.questions[this.questionIndex] || null;
    },

    currentQuestionTypeText(): string {
      if (!this.currentQuestion?.type) {
        return "Не выбрано";
      }

      if (this.isOpenListQuestionType(this.currentQuestion.type)) {
        return "Открытый список";
      }

      if (this.isTextQuestionType(this.currentQuestion.type)) {
        return "Текст";
      }

      const questionType = this.typeQuestions.find((item) => {
        return item.value === this.currentQuestion?.type;
      });

      return questionType?.text || "Не выбрано";
    },

    isRequiredQuestion(): boolean {
      return this.currentQuestion?.skip !== true;
    },

    typeQuestions() {
      return [
        {
          text: "Выпадающий список",
          value: "select",
          variant: "tab",
          active: this.currentQuestion?.type === "select"
        },
        {
          text: "Открытый список",
          value: "radio",
          variant: "tab",
          active: this.isOpenListQuestionType(this.currentQuestion?.type)
        },
        {
          text: "Текст",
          value: "text",
          variant: "tab",
          active: this.isTextQuestionType(this.currentQuestion?.type ?? "")
        },
        {
          text: "Выбор зубов",
          value: "checkbox-dental",
          variant: "tab",
          active: this.currentQuestion?.type === "checkbox-dental"
        },
        {
          text: "Информационный блок",
          value: "information",
          variant: "tab",
          active: this.currentQuestion?.type === "information"
        }
      ] as SelectItem[];
    },
    dataPanel(): DataPanelClues {
      return this.$panelStore.getDataPanelClues;
    }
  },

  watch: {
    "dataPanel.index"(): void {
      this.selectedTabPanel = "Настройки";
    }
  }
});
</script>
